import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, Avatar, Chip,
  Stack, Tabs, Tab, Badge, CircularProgress,
} from '@mui/material';
import {
  Dashboard as OverviewIcon,
  Badge as PersonalIcon,
  WorkHistory as HRIcon,
  AccountBalanceWallet as PayIcon,
  Star as PerformanceIcon,
  AssignmentTurnedIn as ApprovalsIcon,
  Settings as SettingsIcon,
  CameraAlt as CameraIcon,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { useAuthStore } from '@/stores/authStore';
import { authApi } from '@/api/endpoints/auth';
import { portalApi } from '@/api/endpoints/hr';
import { useJurisdiction } from '@/hooks/useJurisdiction';
import { useNotificationStore } from '@/stores/notificationStore';
import { resolveUploadUrl } from '@/utils/format';
import PageHelpIcon from '@/components/PageHelpIcon';
import TenantLogo from '@/components/common/TenantLogo';
import apiClient from '@/api/client';

// Tab components
import OverviewTab from './tabs/OverviewTab';
import PersonalTab from './tabs/PersonalTab';
import HRWorkLifeTab from './tabs/HRWorkLifeTab';
import PayFinancesTab from './tabs/PayFinancesTab';
import SettingsTab from './tabs/SettingsTab';

// External pages embedded
import MyReviewsPage from '@/pages/performance/MyReviewsPage';
import MyApprovalsPage from '@/pages/approvals/MyApprovalsPage';

// Tab index constants
const TAB = {
  OVERVIEW: 0,
  PERSONAL: 1,
  HR: 2,
  PAY: 3,
  PERFORMANCE: 4,
  APPROVALS: 5,
  SETTINGS: 6,
} as const;

export default function ProfilePage() {
  const { currencyCode } = useJurisdiction();
  const qc = useQueryClient();
  const location = useLocation();
  const { user, getActiveCompany } = useAuthStore();
  const activeCompany = getActiveCompany();
  const { showSuccess, showError } = useNotificationStore();
  const [activeTab, setActiveTab] = useState<number>(TAB.OVERVIEW);
  const [subTabOverride, setSubTabOverride] = useState<number | undefined>(undefined);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Allow navigating to a specific tab via location state (e.g. { tab: 3, subTab: 1 })
  useEffect(() => {
    const stateTab = (location.state as any)?.tab;
    const stateSubTab = (location.state as any)?.subTab;
    if (typeof stateTab === 'number' && stateTab >= 0 && stateTab <= 6) {
      setActiveTab(stateTab);
      if (typeof stateSubTab === 'number') {
        setSubTabOverride(stateSubTab);
      }
      // Clear location state so future navigations to /profile work fresh
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  // 2FA enrollment wall (RBAC hardening): the API interceptor redirects here
  // with this flag when a request hit a `2fa_required_for_role` 403. Open the
  // Settings tab (which hosts 2FA enrollment) and clear the flag.
  useEffect(() => {
    if (sessionStorage.getItem('pending_2fa_enroll')) {
      setActiveTab(TAB.SETTINGS);
      sessionStorage.removeItem('pending_2fa_enroll');
    }
  }, []);

  // Employee data
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['auth/me'],
    queryFn: () => authApi.getMe().then(r => r.data.user),
  });

  // Use the portal endpoint (my-profile) as the primary source — it's RLS-safe for
  // any authenticated user and avoids the hr.employees.read permission check that
  // employeesApi.list() requires. Doubles as the avatar cache key shared with Navbar.
  const { data: myProfile, isLoading: empLoading } = useQuery({
    queryKey: ['profile/my-avatar'],
    queryFn: () => portalApi.getMyProfile().then((r) => r.data).catch(() => null),
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: !!user,
  });

  // emp alias kept for tab components that use it; falls back to the admin-list
  // lookup only for admins who need the richer employee schema from the HR list.
  const emp = myProfile ?? null;
  const avatarSrc = resolveUploadUrl((myProfile as any)?.avatar_url);

  // Pending approvals count for badge
  const { data: pendingApprovalsCount } = useQuery({
    queryKey: ['approvals-badge-count'],
    queryFn: async () => {
      try {
        const res = await apiClient.get<{ items: any[] }>('/approvals/pending');
        return res.data.items?.length || 0;
      } catch { return 0; }
    },
    refetchInterval: 60000,
  });

  // Avatar upload
  const avatarMutation = useMutation({
    mutationFn: (file: File) => portalApi.uploadMyAvatar(file),
    onSuccess: () => { showSuccess('Avatar updated'); qc.invalidateQueries({ queryKey: ['auth/me'] }); qc.invalidateQueries({ queryKey: ['profile/my-avatar'] }); },
    onError: (e: any) => showError(e.response?.data?.detail || 'Failed to upload avatar'),
  });

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) avatarMutation.mutate(file);
    e.target.value = '';
  };

  const handleNavigateTab = (tabIndex: number, subTab?: number) => {
    setActiveTab(tabIndex);
    if (subTab !== undefined) setSubTabOverride(subTab);
    else setSubTabOverride(undefined);
  };

  const ini = user ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() : '?';

  return (
    <Box>
      {/* Page Header */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <TenantLogo size={32} showFallback={false} />
        <Typography variant="h4">My Profile</Typography>
        <PageHelpIcon pageKey="profile" />
      </Stack>

      {/* Profile Banner */}
      <Card sx={{ mb: 2 }}>
        <Box sx={{ height: 4, bgcolor: 'primary.main' }} />
        <CardContent sx={{ p: '16px 24px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ position: 'relative', cursor: 'pointer' }} onClick={() => avatarInputRef.current?.click()}>
              <Avatar
                src={avatarSrc}
                sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: TYPE['3xl'], fontWeight: TYPE.bold }}
              >
                {ini}
              </Avatar>
              <Box sx={{
                position: 'absolute', bottom: -2, right: -2,
                width: 24, height: 24, borderRadius: '50%',
                bgcolor: 'primary.main', color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid', borderColor: 'background.paper',
                '&:hover': { bgcolor: 'primary.dark' },
              }}>
                {avatarMutation.isPending ? <CircularProgress size={12} sx={{ color: 'white' }} /> : <CameraIcon sx={{ fontSize: 14 }} />}
              </Box>
              <input ref={avatarInputRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={handleAvatarChange} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: TYPE['2xl'], fontWeight: TYPE.bold }}>{user?.first_name} {user?.last_name}</Typography>
              <Typography sx={{ fontSize: TYPE.md, color: 'text.secondary' }}>{user?.email}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                {activeCompany && <Chip label={activeCompany.role_name} size="small" color="primary" variant="outlined" />}
                {activeCompany && <Chip label={activeCompany.company_name} size="small" variant="outlined" />}
                {emp && <Chip label={emp.designation || 'Staff'} size="small" variant="outlined" />}
              </Stack>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Primary Navigation Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => { setActiveTab(v); setSubTabOverride(undefined); }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2.5,
          '& .MuiTab-root': {
            textTransform: 'none', fontWeight: TYPE.semibold, fontSize: TYPE.md,
            minHeight: 44, py: 0.75,
          },
        }}
      >
        <Tab icon={<OverviewIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Overview" />
        <Tab icon={<PersonalIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Personal" />
        <Tab icon={<HRIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="People & Work Life" />
        <Tab icon={<PayIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Pay & Finances" />
        <Tab icon={<PerformanceIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Performance" />
        <Tab
          icon={
            <Badge badgeContent={pendingApprovalsCount || 0} color="error" max={99}>
              <ApprovalsIcon sx={{ fontSize: 18 }} />
            </Badge>
          }
          iconPosition="start"
          label="Approvals"
        />
        <Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Settings" />
      </Tabs>

      {/* Tab Content */}
      {activeTab === TAB.OVERVIEW && (
        <OverviewTab user={user} emp={emp} activeCompany={activeCompany} onNavigateTab={handleNavigateTab} />
      )}

      {activeTab === TAB.PERSONAL && (
        <PersonalTab user={me || user} emp={emp} activeCompany={activeCompany} meLoading={meLoading} empLoading={empLoading} />
      )}

      {activeTab === TAB.HR && (
        <HRWorkLifeTab emp={emp} currencyCode={currencyCode} initialSubTab={subTabOverride} />
      )}

      {activeTab === TAB.PAY && (
        emp ? (
          <PayFinancesTab emp={emp} currencyCode={currencyCode} initialSubTab={subTabOverride} />
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <PayIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
            <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.semibold, mb: 1 }}>No Employee Record Found</Typography>
            <Typography sx={{ fontSize: TYPE.base, color: 'text.secondary', maxWidth: 400, mx: 'auto' }}>
              Pay & Finances requires an employee record. Contact an administrator to set up your profile.
            </Typography>
          </Box>
        )
      )}

      {activeTab === TAB.PERFORMANCE && (
        <MyReviewsPage embedded />
      )}

      {activeTab === TAB.APPROVALS && (
        <MyApprovalsPage embedded />
      )}

      {activeTab === TAB.SETTINGS && (
        <SettingsTab user={user} emp={emp} />
      )}
    </Box>
  );
}
